/**
 * 통합관리자 — 과금 정책 (요금제)
 * ---------------------------------------------------------------------------
 * 요금제는 "무엇을 얼마에, 얼마나 쓸 수 있게 할 것인가"를 정한다.
 *
 * 화면에서 지키는 것
 *   - 가격은 구독 중인 기업이 있으면 잠근다. 서버도 막지만, 눌러본 뒤
 *     거절당하는 것보다 왜 못 바꾸는지 먼저 보여주는 편이 낫다.
 *   - 초과 허용을 켜면 초과 단가를 반드시 받는다. 비워두면 한도를 넘겨 써도
 *     청구가 0원이 된다.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { useCodes } from '../../lib/codes';
import { useCatalog } from '../../lib/catalog';
import { PageHelp } from '../../components/PageHelp';
import { GUIDE } from '../../lib/guide';
import { PlanVersionModal } from './PlanVersionModal';

const { Text, Paragraph } = Typography;

interface PlanRow {
  plan_id: string;
  product_code: string;
  product_name: string;
  billing_unit: 'FLAT' | 'PER_USER' | 'PER_SITE' | 'PER_UNIT';
  unit_label: string | null;
  included_units: number;
  plan_code: string;
  plan_name: string;
  description: string | null;
  plan_type: string;
  billing_cycle: string;
  currency: string;
  base_price: number;
  unit_price: number;
  min_units: number;
  max_units: number | null;
  trial_days: number;
  is_public: boolean;
  is_active: boolean;
  /**
   * 가격 버전의 시행 기간.
   *
   * 🔴 [버그 수정] 이 두 값을 화면이 버리고 있었다. 그래서 「판매중지」 꼬리표만
   *    없으면 팔리는 줄 알았는데, 실제로는 **시행일이 아직 안 온 버전**이거나
   *    **이미 끝난 버전**이라 고객 요금표에는 나오지 않는 경우가 있었다.
   *    (요금을 올리려고 새 버전을 만들어 두면 그 사이 공백이 생긴다)
   */
  effective_from: string;
  effective_to: string | null;
  active_subscriptions: number;
  modules: string[];
  /**
   * 이 요금제가 속한 **제품**이 팔리는 상태인가.
   *
   * 요금제를 켜 두어도 제품이 내려가 있으면 고객 요금표에도, 가입 화면에도
   * 나오지 않는다(양쪽 다 `WHERE pr.is_active`). 그 사실을 이 화면이 모르면
   * 운영자만 팔리는 줄 알게 된다.
   */
  product_is_active: boolean;
}

/** 한국 날짜로 포맷한다. date 는 시각이 없으므로 타임존을 고정해야 한다. */
const KST_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * 시행일을 YYYY-MM-DD 로 만든다.
 *
 * 🔴 [버그 수정] 서버가 date 컬럼을 ISO 타임스탬프로 내려주던 때가 있었다
 *    (2026-09-21 → "2026-09-20T15:00:00.000Z"). 앞 10자를 자르면 09-20 이라는
 *    **하루 밀린 날짜**가 나온다. 지금은 API 쪽 타입 파서를 고쳐 'YYYY-MM-DD'
 *    로 오지만, 둘 중 어느 모양이 와도 같은 날짜가 나오도록 여기서도 받아 둔다.
 *    (옛 번들·캐시된 응답이 섞여도 화면이 틀리지 않게)
 */
function ymd(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return KST_DATE.format(parsed);
}

/** 오늘(한국 기준) */
function todayKst(): string {
  return KST_DATE.format(new Date());
}

/** 오늘 기준으로 이 가격 버전이 어떤 상태인가 */
type SaleState = 'PRODUCT_OFF' | 'ON_SALE' | 'STOPPED' | 'PRIVATE' | 'SCHEDULED' | 'ENDED';

/**
 * 고객 요금표에 나오는 조건은 넷을 모두 만족할 때다 —
 * 판매중 · 공개 · 시행일 지남 · 종료일 안 지남.
 * 서버의 CURRENT_PLAN_CTE 와 같은 기준이어야 화면과 실제가 어긋나지 않는다.
 */
function saleStateOf(row: PlanRow): SaleState {
  /*
   * 제품이 내려가 있으면 요금제를 아무리 켜 두어도 팔리지 않는다.
   * 고객 요금표도 가입 화면도 `WHERE pr.is_active` 로 먼저 걸러내기 때문이다.
   * 그래서 가장 먼저 본다 — 이 줄이 없으면 운영자만 「판매중」으로 믿게 된다.
   */
  if (row.product_is_active === false) return 'PRODUCT_OFF';

  if (!row.is_active) return 'STOPPED';

  const today = todayKst();
  const from = ymd(row.effective_from);
  const to = ymd(row.effective_to);
  if (from !== null && from > today) return 'SCHEDULED';
  if (to !== null && to <= today) return 'ENDED';

  if (!row.is_public) return 'PRIVATE';
  return 'ON_SALE';
}

const SALE_STATE_META: Record<SaleState, { text: string; color: string; hint: string }> = {
  PRODUCT_OFF: {
    text: '제품 중지',
    color: 'volcano',
    hint: '제품이 내려가 있어 요금제를 켜 두어도 팔리지 않습니다. 고객 → 제품 현황에서 이 제품의 「사용」을 켜야 합니다',
  },
  ON_SALE: { text: '판매중', color: 'green', hint: '고객 요금표에 나옵니다' },
  STOPPED: { text: '판매중지', color: 'default', hint: '신규 가입을 받지 않습니다. 기존 구독은 그대로입니다' },
  PRIVATE: { text: '비공개', color: 'purple', hint: '영업 협의 전용입니다. 요금표에 나오지 않습니다' },
  SCHEDULED: { text: '시행 전', color: 'orange', hint: '시행일이 아직 오지 않아 요금표에 나오지 않습니다' },
  ENDED: { text: '종료됨', color: 'red', hint: '종료일이 지나 요금표에 나오지 않습니다' },
};

interface QuotaRow {
  quota_key: string;
  /** 기본 제공량. 계약 수량과 무관하게 깔아 주는 몫 */
  quota_value: number;
  /** 계약 수량 1 단위당 더해 주는 몫. 실제 한도 = quota_value + quota_per_unit × 수량 */
  quota_per_unit: number;
  unit: string;
  overage_allowed: boolean;
  /** 묶음(overage_step) 하나당 금액(원) */
  overage_price: number | null;
  /** 단가를 매기는 묶음 크기. 1GB 당이면 1073741824, 1개당이면 1 */
  overage_step: number;
  metric_name: string | null;
}

/* =========================================================================
 * 과금 방식에 따라 달라지는 말
 *
 * 「단위당」·「기본 포함 수량」이라고만 적어 두면 무엇의 수인지 알 수 없다.
 * 사용자당 요금제를 만들면서 「단위 표시명」을 비워 두면, 청구서에도 화면에도
 * 「단위 3」이라고 찍힌다. 과금 방식을 고르는 순간 아래 칸들의 말이 함께
 * 바뀌도록 한다.
 * ====================================================================== */
interface UnitWords {
  /** 세는 대상 — 사용자·사업장 */
  noun: string;
  /** 세는 말 — 명·곳·개 */
  counter: string;
  placeholder: string;
}

const UNIT_WORDS: Record<string, UnitWords> = {
  PER_USER: { noun: '사용자', counter: '명', placeholder: '사용자' },
  PER_SITE: { noun: '사업장', counter: '곳', placeholder: '사업장' },
  PER_UNIT: { noun: '단위', counter: '개', placeholder: '예: 장비' },
};

function wordsFor(billingUnit: string, unitLabel?: string | null): UnitWords {
  const base = UNIT_WORDS[billingUnit] ?? UNIT_WORDS.PER_UNIT!;
  // 운영자가 표시명을 적었으면 그 말을 쓴다. 「장비」로 팔면 「장비 3대」여야 한다.
  return unitLabel ? { ...base, noun: unitLabel } : base;
}

/* =========================================================================
 * 용량 단위
 *
 * 저장은 바이트다. 사람이 바이트로 읽고 쓰면 반드시 틀린다 — 5368709120 이
 * 5GB 인지 50GB 인지 세어 봐야 안다. 화면에서는 MB·GB·TB 로 고르게 한다.
 *
 * 초과 단가도 **같은 단위당**으로 받는다. 한도는 GB 칸인데 단가만 바이트당이면
 * 나란히 놓인 10 을 "GB 당 10원" 으로 읽게 되고, 실제로는 바이트당 10원이 되어
 * 1GB 초과에 107억이 청구된다.
 * ====================================================================== */
const BYTE_UNITS = [
  { value: 'MB', factor: 1024 ** 2 },
  { value: 'GB', factor: 1024 ** 3 },
  { value: 'TB', factor: 1024 ** 4 },
] as const;

const DEFAULT_BYTE_UNIT = 'GB';

function byteFactor(unit: string): number {
  return BYTE_UNITS.find((u) => u.value === unit)?.factor ?? 1024 ** 3;
}

/** 묶음 크기를 단위 이름으로. 1073741824 → "GB". 못 찾으면 GB 로 둔다 */
function byteUnitOf(step: number): string {
  return BYTE_UNITS.find((u) => u.factor === step)?.value ?? DEFAULT_BYTE_UNIT;
}

/** 바이트를 딱 떨어지는 가장 큰 단위로 되돌린다. 5368709120 → 5 GB */
function fromBytes(bytes: number): { value: number; unit: string } {
  for (let i = BYTE_UNITS.length - 1; i >= 0; i -= 1) {
    const u = BYTE_UNITS[i]!;
    if (bytes >= u.factor && Number.isInteger(bytes / u.factor)) {
      return { value: bytes / u.factor, unit: u.value };
    }
  }
  return { value: bytes / 1024 ** 3, unit: DEFAULT_BYTE_UNIT };
}

const won = (n: number) => n.toLocaleString('ko-KR', { maximumFractionDigits: 2 });

/**
 * 고르는 목록은 모두 서버가 준다.
 *
 *   제품·모듈·한도 항목  각자의 표가 주인이다 → /admin/catalog
 *   과금 방식·주기·유형  DB 의 CHECK 와 짝인 코드값이다 → 공통코드
 *
 * 예전에는 넷 다 이 파일에 배열로 있었다. 제품을 하나 등록해 놓고 이 배열에
 * 넣는 것을 잊으면 요금제 화면에서 그 제품을 고를 수 없었다.
 */
const CODE_GROUPS = ['BILLING_UNIT', 'BILLING_CYCLE', 'PLAN_TYPE'];

export default function PlanPage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [form] = Form.useForm();
  const codes = useCodes(CODE_GROUPS);
  const catalog = useCatalog();

  const [includeInactive, setIncludeInactive] = useState(false);
  /** 공개 여부 거르기. 서버가 아니라 화면에서 거른다 — 목록이 작고 즉시 반응한다 */
  const [publicFilter, setPublicFilter] = useState<'ALL' | 'PUBLIC' | 'PRIVATE'>('ALL');
  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** 가격 버전을 만들 대상. 열려 있으면 버전 창이 뜬다. */
  const [versionOf, setVersionOf] = useState<PlanRow | null>(null);

  const plans = useQuery<PlanRow[], NormalizedError>({
    queryKey: ['admin', 'plans', includeInactive],
    queryFn: async () =>
      (await apiClient.get<PlanRow[]>('/admin/plans', { params: { includeInactive } })).data,
  });

  const visiblePlans = (plans.data ?? []).filter((p) => {
    if (publicFilter === 'PUBLIC') return p.is_public;
    if (publicFilter === 'PRIVATE') return !p.is_public;
    return true;
  });

  const detail = useQuery<PlanRow & { quotas: QuotaRow[] }, NormalizedError>({
    queryKey: ['admin', 'plans', editing?.plan_id],
    queryFn: async () =>
      (await apiClient.get(`/admin/plans/${editing!.plan_id}`)).data,
    enabled: Boolean(editing),
  });

  const open = creating || Boolean(editing);
  const locked = (editing?.active_subscriptions ?? 0) > 0;

  useEffect(() => {
    if (!open) return;

    if (creating) {
      form.setFieldsValue({
        productCode: 'neco',
        planType: 'STANDARD',
        billingCycle: 'MONTHLY',
        billingUnit: 'FLAT',
        includedUnits: 0,
        basePrice: 0,
        unitPrice: 0,
        minUnits: 1,
        trialDays: 14,
        isPublic: true,
        sortOrder: 100,
        // Hub 묶음은 고르지 않아도 항상 들어간다. 제품 기능만 고르면 된다.
        modules: [],
        quotas: [
          { quotaKey: 'storage.total_bytes', quotaUnit: 'GB', quotaValue: 10, overageAllowed: false },
          { quotaKey: 'user.active_count', quotaValue: 10, overageAllowed: false },
        ],
      });
      return;
    }

    if (detail.data) {
      form.setFieldsValue({
        productCode: detail.data.product_code,
        planCode: detail.data.plan_code,
        planName: detail.data.plan_name,
        billingUnit: detail.data.billing_unit,
        unitLabel: detail.data.unit_label,
        includedUnits: detail.data.included_units,
        description: detail.data.description,
        planType: detail.data.plan_type,
        billingCycle: detail.data.billing_cycle,
        basePrice: detail.data.base_price,
        unitPrice: detail.data.unit_price,
        minUnits: detail.data.min_units,
        maxUnits: detail.data.max_units,
        trialDays: detail.data.trial_days,
        isPublic: detail.data.is_public,
        modules: detail.data.modules ?? [],
        quotas: (detail.data.quotas ?? []).map((q) => {
          if (q.unit !== 'BYTES') {
            return {
              quotaKey: q.quota_key,
              quotaValue: q.quota_value,
              quotaPerUnit: q.quota_per_unit ?? 0,
              overageAllowed: q.overage_allowed,
              overagePrice: q.overage_price,
            };
          }
          /*
           * 바이트 한도는 딱 떨어지는 단위로 보여준다. 12884901888 을 읽고 쓰게
           * 하면 틀리기 쉽다. 단가도 같은 단위당으로 환산해 보여 준다.
           *
           * 보여 줄 단위는 기본 제공량과 수량당 추가량 중 **큰 쪽**으로 고른다.
           * 기본이 0 인 한도(수량당으로만 주는 것)를 기본값만 보고 고르면
           * 5GB 가 5368709120 B 로 찍힌다.
           */
          const base = Number(q.quota_value);
          const perUnit = Number(q.quota_per_unit ?? 0);
          const step = Number(q.overage_step ?? 1);

          /*
           * 보여 줄 단위는 **단가의 묶음 크기**를 먼저 따른다.
           *
           * 한도와 단가가 같은 단위여야 「70GB 까지 · 넘기면 GB당 500원」처럼
           * 읽힌다. 단가가 1GB 묶음인데 한도만 TB 로 보여 주면 「2TB 까지 ·
           * TB당 500원」이 되어 단가가 1000배 싸 보인다.
           * 묶음이 없는(개수) 한도만 한도 값에서 단위를 고른다.
           */
          const unit = step > 1 ? byteUnitOf(step) : fromBytes(Math.max(base, perUnit)).unit;
          const factor = byteFactor(unit);

          return {
            quotaKey: q.quota_key,
            quotaUnit: unit,
            quotaValue: base / factor,
            quotaPerUnit: perUnit / factor,
            overageAllowed: q.overage_allowed,
            /*
             * 단가는 **묶음당 금액 그대로** 쓴다. 환산하지 않는다.
             *
             * 예전에는 바이트당으로 저장해 두고 화면에서 ×1GB 를 했다.
             * 500/2^30 은 소수로 딱 떨어지지 않아 저장에서 잘리고, 되읽으면
             * 499.9997 이 되었다. 이제는 묶음 크기(overage_step)를 따로
             * 들고 있어 500 이 500 으로 돌아온다.
             */
            overagePrice: q.overage_price,
          };
        }),
      });
    }
  }, [open, creating, detail.data, form]);

  const close = () => {
    setCreating(false);
    setEditing(null);
    setFormError(null);
    form.resetFields();
  };

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...values,
        quotas: (values.quotas ?? []).map((q: Record<string, unknown>) => {
          const unit = catalog.unitOf(String(q.quotaKey));
          /*
           * 한도는 화면이 고른 단위(GB 등)로 받아 바이트로 바꿔 저장한다.
           * 정수끼리의 곱이라 값이 깨지지 않는다.
           *
           * 단가는 **나누지 않는다**. 대신 그 단위를 묶음 크기로 함께 보낸다.
           *   GB 당 500원  →  overageStep 1073741824, overagePrice 500
           * 예전처럼 ÷1GB 를 하면 0.000000465661... 이 되어 저장에서 잘리고,
           * 되읽을 때 499.9997 로 돌아왔다. 청구 금액에도 소수가 붙었다.
           */
          const factor =
            unit === 'BYTES' ? byteFactor(String(q.quotaUnit ?? DEFAULT_BYTE_UNIT)) : 1;
          return {
            quotaKey: q.quotaKey,
            unit,
            quotaValue: Number(q.quotaValue ?? 0) * factor,
            quotaPerUnit: Number(q.quotaPerUnit ?? 0) * factor,
            overageAllowed: Boolean(q.overageAllowed),
            overageStep: factor,
            overagePrice: q.overageAllowed ? Number(q.overagePrice ?? 0) : null,
          };
        }),
      };

      if (creating) {
        await apiClient.post('/admin/plans', payload);
        message.success('요금제를 등록했습니다.');
      } else {
        // 가격은 잠겨 있으면 보내지 않는다. 서버가 거절할 값을 굳이 실어 보낼 이유가 없다.
        if (locked) {
          delete payload.basePrice;
          delete payload.unitPrice;
        }
        delete payload.planCode; // 식별자는 바꿀 수 없다
        delete payload.productCode; // 제품도 바꿀 수 없다
        await apiClient.patch(`/admin/plans/${editing!.plan_id}`, payload);
        message.success('요금제를 수정했습니다.');
      }

      close();
      await client.invalidateQueries({ queryKey: ['admin', 'plans'] });
    } catch (err) {
      setFormError(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = (row: PlanRow) => {
    let reason = '';
    Modal.confirm({
      title: `${row.plan_name} — ${row.is_active ? '판매 중지' : '판매 재개'}`,
      width: 520,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={
              row.is_active
                ? '신규 가입만 막습니다'
                : '이 요금제로 다시 가입할 수 있게 됩니다'
            }
            description={
              row.is_active
                ? `이미 구독 중인 ${row.active_subscriptions}곳은 그대로 유지됩니다. ` +
                  '지난 청구서의 근거가 되므로 요금제 행은 남습니다. ' +
                  '한 번도 쓰인 적이 없는 요금제라면 중지한 뒤 삭제할 수 있습니다.'
                : undefined
            }
          />
          <Input.TextArea
            rows={2}
            placeholder="예: 2026년 신규 요금제로 대체"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </Space>
      ),
      okText: row.is_active ? '판매 중지' : '판매 재개',
      okButtonProps: { danger: row.is_active },
      cancelText: '취소',
      async onOk() {
        if (!reason.trim()) {
          message.error('사유를 입력해 주세요.');
          return Promise.reject(new Error('reason required'));
        }
        await apiClient.patch(`/admin/plans/${row.plan_id}/active`, {
          isActive: !row.is_active,
          reason: reason.trim(),
        });

        // 중지하면 기본 목록(판매중만)에서 사라져 "눌렸는지" 를 알 수 없었다.
        // 바뀐 줄이 눈에 남도록 중지한 자리에서는 포함 보기를 켜 둔다.
        if (row.is_active) setIncludeInactive(true);

        message.success(
          row.is_active
            ? '판매를 중지했습니다. 고객 요금표에서 내려갑니다.'
            : '판매를 재개했습니다.',
        );
        await client.invalidateQueries({ queryKey: ['admin', 'plans'] });
      },
    });
  };

  /**
   * 요금제를 지운다.
   *
   * ## 왜 판매중지된 것만 지울 수 있나
   * 삭제는 되돌릴 수 없고, 한 번 팔린 요금제를 지우면 그 달 청구서의 금액
   * 근거가 사라진다. 그래서 **쓰인 적 없는 요금제**만 지워진다. 시험 삼아
   * 만들었거나 값을 잘못 넣어 새로 만든 것들이 목록에 쌓이는데, 지울 길이
   * 없으면 「판매중지」 목록이 계속 길어져 진짜 과거 요금제를 못 찾는다.
   *
   * ## 화면은 막지 않는다. 서버가 막는다
   * 여기서는 구독 수를 보고 미리 거르지 않는다. 이 목록은 몇 초 전의 것이고
   * 그 사이에 가입신청이 들어올 수 있다. 눌러 보고 서버가 거절하면 **무엇이
   * 걸렸는지** 그대로 보여 준다 — 「구독 3건, 가입신청 1건」처럼.
   */
  const removePlan = (row: PlanRow) => {
    let reason = '';
    Modal.confirm({
      title: `${row.plan_name} — 삭제`,
      width: 520,
      okType: 'danger',
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="되돌릴 수 없습니다"
            description={
              '사용량 한도와 모듈 구성도 함께 지워집니다. ' +
              '구독·가입신청·사용자 수 초과 승인이 하나라도 있으면 서버가 거절합니다 — ' +
              '지난 청구서의 금액 근거가 사라지기 때문입니다.'
            }
          />
          <Input.TextArea
            rows={2}
            placeholder="예: 시험용으로 만든 요금제, 사용 이력 없음"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </Space>
      ),
      okText: '삭제',
      cancelText: '취소',
      async onOk() {
        if (!reason.trim()) {
          message.error('사유를 입력해 주세요.');
          return Promise.reject(new Error('reason required'));
        }
        try {
          await apiClient.delete(`/admin/plans/${row.plan_id}`, {
            params: { reason: reason.trim() },
          });
        } catch (err) {
          // 서버가 거절한 까닭(구독 n건 …)을 그대로 띄운다. 여기서 문구를
          // 다시 지어내면 실제로 무엇이 걸렸는지 알 수 없게 된다.
          message.error(errorText(err as NormalizedError));
          return Promise.reject(err);
        }
        message.success(`「${row.plan_name}」을 삭제했습니다.`);
        await client.invalidateQueries({ queryKey: ['admin', 'plans'] });
      },
    });
  };

  const columns: ColumnsType<PlanRow> = [
    {
      title: '제품 / 요금제',
      key: 'plan',
      render: (_, row) => {
        const state = SALE_STATE_META[saleStateOf(row)];
        return (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <Tag color="blue">{row.product_name}</Tag>
              <strong>{row.plan_name}</strong>
              {/*
                꼬리표를 하나로 모은다. 예전에는 '판매중지'·'비공개' 두 개만
                따로 띄워, 아무것도 없으면 팔리는 줄 알았다. 시행 전·종료됨은
                보이지 않아 "왜 요금표에 없지" 를 알 수 없었다.
              */}
              <Tooltip title={state.hint}>
                <Tag color={state.color}>{state.text}</Tag>
              </Tooltip>
              {/* 공개 여부는 판매 상태와 별개라 따로 한 번 더 알린다 */}
              {!row.is_public && row.is_active && <Tag color="purple">비공개</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.plan_code} · {row.plan_type}
            </Text>
          </Space>
        );
      },
    },
    {
      /*
       * 시행일은 "왜 요금표에 안 나오지" 를 혼자 설명해 주는 값이라 목록에
       * 반드시 있어야 한다. 시행일·종료일을 따로 줄을 나눠 적는다 — 한 줄에
       * 붙여 두면 좁은 열에서 줄바꿈되어 어느 쪽이 시행일인지 읽기 어렵다.
       */
      title: '시행일',
      key: 'effective',
      width: 150,
      render: (_, row) => {
        const from = ymd(row.effective_from);
        const to = ymd(row.effective_to);
        const state = saleStateOf(row);
        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 12 }}>
              {from ?? '—'}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {to === null ? '종료일 없음' : `${to} 종료`}
            </Text>
            {state === 'SCHEDULED' && (
              <Text type="warning" style={{ fontSize: 11 }}>
                아직 시행 전
              </Text>
            )}
            {state === 'ENDED' && (
              <Text type="danger" style={{ fontSize: 11 }}>
                기간 종료
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '가격',
      key: 'price',
      width: 200,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>기본 {row.base_price.toLocaleString('ko-KR')}원</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.billing_unit === 'FLAT'
              ? '정액'
              : `${row.unit_label ?? '단위'}당 ${row.unit_price.toLocaleString('ko-KR')}원`}{' · '}
            {row.billing_cycle === 'MONTHLY' ? '월' : row.billing_cycle}
          </Text>
        </Space>
      ),
    },
    {
      title: '수량',
      key: 'units',
      width: 110,
      render: (_, row) => `${row.min_units} ~ ${row.max_units ?? '무제한'}`,
    },
    {
      title: '포함 모듈',
      key: 'modules',
      render: (_, row) => (
        <Space size={4} wrap>
          {(row.modules ?? []).slice(0, 4).map((m) => (
            <Tag key={m} style={{ fontSize: 11 }}>
              {catalog.moduleLabel(m)}
            </Tag>
          ))}
          {(row.modules ?? []).length > 4 && (
            <Tooltip title={(row.modules ?? []).join(', ')}>
              <Tag style={{ fontSize: 11 }}>+{(row.modules ?? []).length - 4}</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '구독',
      dataIndex: 'active_subscriptions',
      key: 'subs',
      width: 80,
      render: (v: number) => (v > 0 ? <Tag color="green">{v}곳</Tag> : <Text type="secondary">-</Text>),
    },
    {
      title: '관리',
      key: 'actions',
      // 판매중지된 줄에는 「삭제」가 하나 더 붙는다. 230 이면 줄바꿈이 났다.
      width: 290,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="link" onClick={() => setEditing(row)}>
            수정
          </Button>
          <Tooltip title="가격만 다른 새 버전을 만듭니다. 기존 구독의 가격은 그대로입니다.">
            <Button size="small" type="link" onClick={() => setVersionOf(row)}>
              가격 버전
            </Button>
          </Tooltip>
          <Button size="small" type="link" danger={row.is_active} onClick={() => toggleActive(row)}>
            {row.is_active ? '판매중지' : '재개'}
          </Button>
          {/*
            삭제는 판매중지된 줄에만 내놓는다. 판매 중인 요금제 옆에 두면
            「판매중지」 바로 옆에 붙어 잘못 누르기 쉽다. 서버도 같은 조건을
            검사하므로 이 버튼이 없다고 지울 수 있는 것은 아니다.
          */}
          {!row.is_active && (
            <Tooltip title="한 번도 쓰인 적이 없는 요금제만 지워집니다">
              <Button size="small" type="link" danger onClick={() => removePlan(row)}>
                삭제
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space wrap size="middle">
          <Checkbox
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          >
            판매 중지된 요금제 포함
          </Checkbox>
          {/*
            공개 여부는 판매 여부와 다른 스위치다. 둘을 같이 걸러 볼 수 있어야
            "왜 요금표에 안 나오지" 를 한 화면에서 좁힐 수 있다.
          */}
          <Select
            value={publicFilter}
            onChange={setPublicFilter}
            style={{ width: 150 }}
            options={[
              { value: 'ALL', label: '공개 여부: 전체' },
              { value: 'PUBLIC', label: '공개만' },
              { value: 'PRIVATE', label: '비공개만' },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void client.invalidateQueries({ queryKey: ['admin', 'plans'] })}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            요금제 등록
          </Button>
        </Space>
      </Card>

      {plans.isError && <Alert type="error" showIcon message={errorText(plans.error)} />}

      <Table<PlanRow>
        rowKey="plan_id"
        columns={columns}
        dataSource={visiblePlans}
        loading={plans.isLoading}
        size="middle"
        scroll={{ x: 1100 }}
        pagination={false}
        locale={{ emptyText: '등록된 요금제가 없습니다.' }}
      />

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        요금제는 삭제하지 않고 판매 중지로 관리합니다. 이미 발행한 청구서가 요금제를 참조하므로,
        지우면 "이 금액이 왜 나왔는지" 설명할 수 없게 됩니다.
      </Paragraph>

      <Modal
        open={open}
        /*
          설명을 창 안에서도 꺼낼 수 있게 둔다. 값을 넣다가 "이 칸이 뭐지" 가
          생기는 자리가 여기인데, 화면 오른쪽 위 물음표는 창에 가려 보이지 않는다.
        */
        title={
          <Space size={6}>
            <span>{creating ? '요금제 등록' : `요금제 수정 — ${editing?.plan_name}`}</span>
            <PageHelp title="요금제 — 화면 설명">{GUIDE.plans?.body}</PageHelp>
          </Space>
        }
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        onCancel={close}
        onOk={save}
        width={1040}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {formError && <Alert type="error" showIcon message={formError} />}
          {locked && (
            <Alert
              type="warning"
              showIcon
              message={`구독 중인 기업이 ${editing?.active_subscriptions}곳 있어 가격을 바꿀 수 없습니다`}
              description="지금 고치면 이미 발행한 청구서의 근거가 달라집니다. 「새 가격 버전」으로 시행일이 다른 버전을 만들고 기업별로 옮기세요."
              action={
                <Button
                  size="small"
                  onClick={() => {
                    setVersionOf(editing as PlanRow);
                    setEditing(null);
                  }}
                >
                  새 가격 버전
                </Button>
              }
            />
          )}

          {/*
            이 가격 버전이 언제부터 적용되는지.
            목록에서 「판매중」으로 보여도 시행일이 안 왔으면 고객 요금표에는
            나오지 않는다. 그 사실을 고치는 자리에서 가장 먼저 보여 준다.
            시행일은 등록 때 정해지고 수정으로는 바뀌지 않으므로 입력칸이 아닌
            읽기 전용 정보로 적는다 — 바꾸려면 「새 가격 버전」을 만들어야 한다.
          */}
          {editing && (
            <Descriptions
              size="small"
              bordered
              column={3}
              style={{ marginBottom: 16 }}
              items={[
                {
                  key: 'from',
                  label: '시행일',
                  children: <strong>{ymd(editing.effective_from) ?? '—'}</strong>,
                },
                {
                  key: 'to',
                  label: '종료일',
                  children: ymd(editing.effective_to) ?? (
                    <Text type="secondary">없음 (계속 적용)</Text>
                  ),
                },
                {
                  key: 'state',
                  label: '지금 상태',
                  children: (
                    <Tooltip title={SALE_STATE_META[saleStateOf(editing)].hint}>
                      <Tag color={SALE_STATE_META[saleStateOf(editing)].color}>
                        {SALE_STATE_META[saleStateOf(editing)].text}
                      </Tag>
                    </Tooltip>
                  ),
                },
              ]}
            />
          )}

          {editing && saleStateOf(editing) !== 'ON_SALE' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`이 요금제는 지금 고객 요금표에 나오지 않습니다 — ${SALE_STATE_META[saleStateOf(editing)].text}`}
              description={SALE_STATE_META[saleStateOf(editing)].hint}
            />
          )}

          <Form form={form} layout="vertical" requiredMark={false}>
            {/* ───────────────────── 1. 무엇을 파는가 ───────────────────── */}
            <Card size="small" title="기본 정보" style={{ marginBottom: 16 }}>
              <Space size="middle" style={{ display: 'flex' }} align="start">
                <Form.Item
                  name="productCode"
                  label="판매 제품"
                  rules={[{ required: true, message: '제품을 선택하세요' }]}
                  style={{ flex: 1 }}
                  extra={creating ? undefined : '등록 후 바꿀 수 없습니다'}
                >
                  <Select disabled={!creating} options={catalog.products} />
                </Form.Item>
                <Form.Item
                  name="planCode"
                  label="요금제 코드"
                  rules={[{ required: true, message: '코드를 입력하세요' }]}
                  style={{ flex: 1 }}
                  extra={creating ? '영문 대문자·밑줄. 청구서에 남습니다' : '바꿀 수 없습니다'}
                >
                  <Input placeholder="PROFESSIONAL" disabled={!creating} />
                </Form.Item>
                <Form.Item
                  name="planName"
                  label="표시 이름"
                  rules={[{ required: true, message: '이름을 입력하세요' }]}
                  style={{ flex: 1 }}
                  extra="고객 요금표에 보이는 이름"
                >
                  <Input placeholder="프로페셔널" />
                </Form.Item>
                <Form.Item name="planType" label="유형" style={{ width: 150 }}>
                  <Select options={codes.options('PLAN_TYPE')} />
                </Form.Item>
              </Space>

              <Form.Item
                name="description"
                label="설명"
                extra="고객 요금표의 요금제 이름 아래에 그대로 나옵니다"
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea rows={2} maxLength={1000} placeholder="어떤 고객을 위한 요금제인지" />
              </Form.Item>
            </Card>


            {/* ───────────────────── 2. 얼마를 받는가 ───────────────────── */}
            <Card size="small" title="가격" style={{ marginBottom: 16 }}>
              <Space size="middle" wrap style={{ display: 'flex' }} align="start">
                <Form.Item name="billingCycle" label="청구 주기">
                  <Select style={{ width: 130 }} options={codes.options('BILLING_CYCLE')} />
                </Form.Item>
                <Form.Item
                  name="billingUnit"
                  label="과금 방식"
                  extra="무엇을 세어 과금할지 — 고르면 아래 칸의 이름이 함께 바뀝니다"
                >
                  <Select style={{ width: 190 }} options={codes.options('BILLING_UNIT')} />
                </Form.Item>
                <Form.Item
                  name="basePrice"
                  label="기본료(원)"
                  extra="수량과 상관없이 매 주기 붙는 고정 금액"
                >
                  {/* 값 타입을 number 로 고정한다. 지정하지 않으면 antd 가 min 의
                      리터럴 타입(0)으로 추론해 parser 반환형과 충돌한다. */}
                  <InputNumber<number>
                    min={0}
                    step={10000}
                    disabled={locked}
                    style={{ width: 170 }}
                    addonAfter="원"
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(v) => Number((v ?? '').replace(/,/g, ''))}
                  />
                </Form.Item>
              </Space>

              {/*
                수량에 딸린 칸들은 한 덩어리다 — 단위 표시명·단위당·포함 수량·
                최소·최대는 따로 떼어 놓으면 무엇과 무엇이 짝인지 알 수 없다.
                정액이면 아예 쓰이지 않으므로 보여 주지 않는다. 보여 주면 값을
                넣게 되고, 넣은 값이 청구서에 안 붙어 "왜 안 되지" 가 된다.
              */}
              <Form.Item
                noStyle
                shouldUpdate={(a, b) =>
                  a.billingUnit !== b.billingUnit || a.unitLabel !== b.unitLabel
                }
              >
                {({ getFieldValue }) => {
                  const mode = String(getFieldValue('billingUnit') ?? 'FLAT');

                  if (mode === 'FLAT') {
                    return (
                      <Alert
                        type="info"
                        showIcon
                        message="정액 요금제 — 수량과 상관없이 기본료만 받습니다"
                        description={
                          <>
                            사용자나 사업장 수에 따라 금액이 달라져야 한다면 과금 방식을{' '}
                            <b>「사용자당」·「사업장당」</b>으로 바꾸세요. 정액으로 둔 채 단위당
                            금액을 적어 두면 <b>청구서에 붙지 않습니다.</b>
                          </>
                        }
                      />
                    );
                  }

                  const w = wordsFor(mode, getFieldValue('unitLabel'));
                  const base = UNIT_WORDS[mode] ?? UNIT_WORDS.PER_UNIT!;

                  return (
                    <>
                      <Divider orientation="left" plain style={{ marginTop: 0 }}>
                        {w.noun} 수에 따라 더 받는 요금
                      </Divider>
                      <Space size="middle" wrap style={{ display: 'flex' }} align="start">
                        <Form.Item
                          name="unitLabel"
                          label="단위 표시명"
                          extra={`청구서에 쓸 말 · 비우면 「${base.noun}」`}
                        >
                          <Input style={{ width: 130 }} placeholder={base.placeholder} />
                        </Form.Item>
                        <Form.Item
                          name="unitPrice"
                          label={`${w.noun} 1${w.counter}당(원)`}
                          extra={`포함 수를 넘는 ${w.noun}마다 더 붙습니다`}
                        >
                          <InputNumber<number>
                            min={0}
                            step={1000}
                            disabled={locked}
                            style={{ width: 170 }}
                            addonAfter="원"
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(v) => Number((v ?? '').replace(/,/g, ''))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="includedUnits"
                          label={`기본료에 포함된 ${w.noun} 수`}
                          extra="여기까지는 더 받지 않습니다"
                        >
                          <InputNumber min={0} style={{ width: 150 }} addonAfter={w.counter} />
                        </Form.Item>
                        <Form.Item
                          name="minUnits"
                          label={`최소 ${w.noun} 수`}
                          extra="가입하면 이 수로 시작합니다"
                        >
                          <InputNumber min={1} style={{ width: 130 }} addonAfter={w.counter} />
                        </Form.Item>
                        <Form.Item
                          name="maxUnits"
                          label={`최대 ${w.noun} 수`}
                          extra="비우면 무제한"
                        >
                          <InputNumber min={1} style={{ width: 130 }} addonAfter={w.counter} />
                        </Form.Item>
                      </Space>
                    </>
                  );
                }}
              </Form.Item>

              {/*
                기본료와 단위당 비용을 말로만 적어 두면 안 와닿는다.
                지금 입력한 값으로 실제 청구액이 얼마가 되는지 그 자리에서 계산해
                보여 준다 — 숫자를 바꿀 때마다 따라 움직인다.
              */}
              <Form.Item
                noStyle
                shouldUpdate={(a, b) =>
                  a.basePrice !== b.basePrice ||
                  a.unitPrice !== b.unitPrice ||
                  a.includedUnits !== b.includedUnits ||
                  a.minUnits !== b.minUnits ||
                  a.billingUnit !== b.billingUnit ||
                  a.unitLabel !== b.unitLabel
                }
              >
                {({ getFieldValue }) => {
                  const base = Number(getFieldValue('basePrice') ?? 0);
                  const unit = Number(getFieldValue('unitPrice') ?? 0);
                  const included = Number(getFieldValue('includedUnits') ?? 0);
                  const min = Number(getFieldValue('minUnits') ?? 1);
                  const mode = String(getFieldValue('billingUnit') ?? 'FLAT');
                  const w = wordsFor(mode, getFieldValue('unitLabel'));

                  const flat = mode === 'FLAT';
                  // 포함 수량보다 적게 시작하면 더 받을 것이 없다.
                  const sample = Math.max(min, included + 2);
                  const billable = Math.max(0, sample - included);
                  const total = flat ? base : base + billable * unit;

                  return (
                    <Alert
                      type="success"
                      style={{ marginTop: 12 }}
                      message="이 값으로 청구하면"
                      description={
                        <div style={{ fontSize: 13, lineHeight: 1.9 }}>
                          {flat ? (
                            <>
                              <b>매 주기 {won(base)}원</b> — 수량과 무관합니다{' '}
                              <span style={{ color: '#8c8c8c' }}>(부가세 별도)</span>
                            </>
                          ) : (
                            <>
                              <div>
                                청구액 = 기본료 + ({w.noun} 수 − 포함 {included}
                                {w.counter}) × {won(unit)}원
                              </div>
                              <div style={{ marginTop: 6 }}>
                                {w.noun} {sample}
                                {w.counter}이면 →{' '}
                                <b>
                                  {won(base)} + {billable} × {won(unit)} = {won(total)}원
                                </b>{' '}
                                <span style={{ color: '#8c8c8c' }}>(부가세 별도)</span>
                              </div>
                            </>
                          )}
                          <div style={{ marginTop: 6, color: '#8c8c8c' }}>
                            ※ 여기서 말하는 「{w.noun} 수」는 실제 쓰는 수를 자동으로 센 값이
                            아니라 <b>구독에 등록해 둔 수량</b>입니다. 실제 사용량으로 더 받으려면
                            아래 <b>사용량 한도</b>를 쓰세요.
                          </div>
                        </div>
                      }
                    />
                  );
                }}
              </Form.Item>
            </Card>

            {/* ───────────────────── 3. 언제 팔리는가 ───────────────────── */}
            <Card size="small" title="판매 조건" style={{ marginBottom: 16 }}>
              <Space size="large" wrap style={{ display: 'flex' }} align="start">
                <Form.Item
                  name="trialDays"
                  label="무료 체험 기간"
                  extra="0 보다 크면 요금표에 「○일 무료 체험」이 붙습니다"
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber min={0} max={365} style={{ width: 140 }} addonAfter="일" />
                </Form.Item>
                <Form.Item
                  name="isPublic"
                  label="고객에게 공개"
                  valuePropName="checked"
                  extra="끄면 요금표에서 사라집니다 (영업 협의 전용)"
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
              </Space>
            </Card>

            {/* ───────────────────── 4. 무엇을 쓸 수 있는가 ───────────────────── */}
            <Card size="small" title="포함 기능" style={{ marginBottom: 16 }}>
              <Form.Item
                noStyle
                shouldUpdate={(a, b) => a.productCode !== b.productCode}
              >
                {({ getFieldValue }) => (
                  <ModuleField productCode={String(getFieldValue('productCode') ?? '')} />
                )}
              </Form.Item>
            </Card>

            {/* ───────────────────── 5. 얼마나 쓸 수 있는가 ───────────────────── */}
            <Card size="small" title="사용량 한도" style={{ marginBottom: 0 }}>
              <Paragraph type="secondary" style={{ fontSize: 12 }}>
                한도를 넘겨 쓴 만큼을 <b>청구서에 자동으로 더합니다.</b> 초과 허용을 끄면 한도에
                도달한 순간 막습니다(청구하지 않습니다). 여기 있는 항목은 제품이 실제로 올려 주는
                값만 보여 줍니다 — 올라오지 않는 값에 한도를 걸면 영원히 과금되지 않습니다.
              </Paragraph>

              <Form.Item
                noStyle
                shouldUpdate={(a, b) => a.productCode !== b.productCode}
              >
                {({ getFieldValue }) => (
                  <QuotaList productCode={String(getFieldValue('productCode') ?? '')} />
                )}
              </Form.Item>
            </Card>
          </Form>
        </Space>
      </Modal>

      <PlanVersionModal plan={versionOf} onClose={() => setVersionOf(null)} />
    </Space>
  );
}

/**
 * 포함 기능 고르기.
 *
 * <para>
 * 예전에는 모든 제품의 모듈을 한 목록에 섞어 보여 줬다. 그래서 WORK&ECO
 * 요금제에 「고객 관리」(CRM 기능)를 넣을 수 있었다 — 넣어도 아무 일이
 * 일어나지 않지만, 요금표에는 제공 기능으로 찍힌다. 파는 것과 주는 것이
 * 어긋나는 쪽이 더 나쁘다.
 * </para>
 * <para>
 * 그래서 **이 제품의 기능**과 **모든 제품이 함께 쓰는 공통 기능**만 고르게 한다.
 * 이미 저장된 것 중 여기에 해당하지 않는 것은 숨기지 않고 「확인 필요」로
 * 따로 모아 보여 준다. 숨기면 잘못 들어간 것을 영영 모른다.
 * </para>
 */
function ModuleField({ productCode }: { productCode: string }) {
  const catalog = useCatalog();
  const form = Form.useFormInstance();
  const selected: string[] = Form.useWatch('modules', form) ?? [];

  const productName =
    catalog.products.find((p) => p.value === productCode)?.label.replace(/\s*\(.*\)$/, '') ??
    '이 제품';

  const groups = useMemo(() => {
    /*
     * 목록은 전부 서버가 준다(/admin/catalog). 제품의 상위 메뉴가 그대로
     * 모듈로 등록되어 있어, 메뉴가 늘면 여기도 따라 는다 — 화면에 적어 두면
     * 제품에 메뉴를 추가할 때마다 이 파일을 고쳐야 한다.
     *
     * Hub 는 「WORK&HUB 기본 기능」 하나로만 온다. 내부 모듈(기업 관리·파일
     * 관리·감사로그…)은 고를 수 있는 것처럼 보여도 뺄 수 없어서 서버가
     * 감춘다(is_plan_visible).
     */
    const mine = catalog.modules.filter((m) => m.productCode === productCode);
    const common = catalog.modules.filter((m) => m.productCode === 'platform');

    const known = new Set([...mine, ...common].map((m) => m.value));
    const stray = selected.filter((v) => !known.has(v));

    const out: Array<{ label: string; options: Array<{ value: string; label: string }> }> = [];
    if (mine.length) {
      out.push({
        label: `${productName} 기능 (제품의 상위 메뉴)`,
        options: mine.map((m) => ({ value: m.value, label: m.label })),
      });
    }
    if (common.length) {
      out.push({
        label: '공통 기능 (모든 제품에 함께 제공)',
        options: common.map((m) => ({ value: m.value, label: m.label })),
      });
    }
    if (stray.length) {
      out.push({
        label: '⚠ 지금 고를 수 없는 기능 — 빼는 것이 좋습니다',
        options: stray.map((v) => ({ value: v, label: catalog.moduleLabel(v) })),
      });
    }
    return out;
  }, [catalog, productCode, productName, selected]);

  const strayCount = groups.find((g) => g.label.startsWith('⚠'))?.options.length ?? 0;

  return (
    <>
      <Form.Item
        name="modules"
        label="이 요금제가 제공하는 기능"
        extra="고른 기능이 고객 요금표의 체크 목록에 그대로 나오고, 제품에서 그 화면을 열 수 있는지를 결정합니다"
        style={{ marginBottom: strayCount ? 12 : 8 }}
      >
        <Select
          mode="multiple"
          options={groups}
          placeholder="제공할 기능 선택"
          optionFilterProp="label"
        />
      </Form.Item>

      {strayCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`지금 고를 수 없는 기능이 ${strayCount}개 들어 있습니다`}
          description="다른 제품의 기능이거나 내려간 기능입니다. 이 제품에는 그 화면이 없어 실제로 쓸 수 없는데 요금표에는 제공 기능으로 찍힙니다. 빼 주세요."
        />
      )}

      <Text type="secondary" style={{ fontSize: 12 }}>
        제품 기능 목록은 <b>그 제품의 상위 메뉴</b>를 그대로 따릅니다. 「WORK&HUB 기본 기능」은
        계정·조직·구성원·권한·파일·구독 조회를 묶은 것으로, <b>빼더라도 항상 포함</b>됩니다 —
        Hub 는 통째로 제공되는 묶음입니다.
      </Text>
    </>
  );
}

/** 한도 줄들의 칸 너비. 머리글과 각 줄이 같은 값을 써야 세로가 맞는다. */
const QUOTA_GRID = '1fr 130px 130px 76px 92px 190px 32px';

/**
 * 한도 목록.
 *
 * 줄마다 Space 로 늘어놓으면 「초과 허용」을 켤 때만 칸이 하나 늘어나 줄끼리
 * 세로가 어긋난다. 격자로 고정하고 머리글을 한 줄 올린다.
 */
function QuotaList({ productCode }: { productCode: string }) {
  const catalog = useCatalog();

  /*
   * 이 제품이 실제로 올리는 지표 + 플랫폼이 직접 재는 지표만.
   * 올라오지 않는 지표에 한도를 걸면 사용량이 0 으로 잡혀 영원히 과금되지 않는다.
   */
  const options = useMemo(
    () =>
      catalog.metrics
        .filter((m) => m.productCode === productCode || m.productCode === 'platform')
        .map((m) => ({
          value: m.value,
          label: m.label,
          title: m.description ?? undefined,
        })),
    [catalog.metrics, productCode],
  );

  return (
    <Form.List name="quotas">
      {(fields, { add, remove }) => (
        <div>
          {fields.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: QUOTA_GRID,
                gap: 8,
                marginBottom: 6,
                fontSize: 12,
                color: '#8c8c8c',
              }}
            >
              <span>한도 항목</span>
              <span>기본 제공</span>
              <span>수량당 추가</span>
              <span>단위</span>
              <span>초과</span>
              <span>초과 단가</span>
              <span />
            </div>
          )}

          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {fields.map((field) => (
              <QuotaRowFields
                key={field.key}
                field={field}
                options={options}
                onRemove={() => remove(field.name)}
              />
            ))}
          </Space>

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => add({ overageAllowed: false, quotaUnit: DEFAULT_BYTE_UNIT })}
            block
            style={{ marginTop: 10 }}
          >
            한도 항목 추가
          </Button>
        </div>
      )}
    </Form.List>
  );
}

/**
 * 한도 한 줄.
 *
 * <para>
 * 초과 허용을 끄면 단가 입력을 감춘다. 켜져 있는데 비어 있으면 서버가
 * 거절하므로 화면에서 먼저 필수로 만든다.
 * </para>
 * <para>
 * 용량은 MB·GB·TB 를 고르게 하고 **단가도 같은 단위당**으로 받는다. 예전에는
 * 한도만 GB 였고 단가는 바이트당이었다. 나란히 놓인 두 칸의 단위가 다르면
 * 반드시 같은 단위로 읽게 된다 — 「10」을 GB 당 10원으로 적었는데 바이트당
 * 10원으로 저장되어, 1GB 초과에 107억이 청구될 상태였다.
 * </para>
 * <para>
 * 그래서 넣은 값으로 **실제 얼마가 청구되는지**를 줄 아래에 바로 계산해 준다.
 * 자릿수가 틀리면 금액에서 눈에 띈다.
 * </para>
 */
function QuotaRowFields({
  field,
  options,
  onRemove,
}: {
  field: { key: number; name: number };
  options: Array<{ value: string; label: string; title?: string }>;
  onRemove: () => void;
}) {
  const form = Form.useFormInstance();
  const catalog = useCatalog();

  const quotaKey = String(Form.useWatch(['quotas', field.name, 'quotaKey'], form) ?? '');
  const overageAllowed = Form.useWatch(['quotas', field.name, 'overageAllowed'], form);
  const quotaValue = Number(Form.useWatch(['quotas', field.name, 'quotaValue'], form) ?? 0);
  const quotaPerUnit = Number(Form.useWatch(['quotas', field.name, 'quotaPerUnit'], form) ?? 0);
  const overagePrice = Number(Form.useWatch(['quotas', field.name, 'overagePrice'], form) ?? 0);

  // 요금제의 단위 표시명(사용자·사업장)을 그대로 쓴다. 「수량당」이라고만
  // 적으면 무엇의 수인지 알 수 없다.
  const perUnitLabel = String(form.getFieldValue('unitLabel') || '수량');
  // 10 수량일 때 실제로 얼마가 되는지 보여 줘서 자릿수 실수를 잡는다.
  // 10 은 **예시**다. 화면에도 「(예시)」라고 밝혀 적는다.
  const limitAtTen = quotaValue + quotaPerUnit * 10;

  /*
   * 「기본 0 + 수량당 1」은 한도가 계약 수량과 **정확히 같다**는 뜻이다.
   * 사용자 수·사업장 수처럼 이미 요금으로 받고 있는 항목이 여기 해당한다.
   * 이때 예시 숫자(10)를 보여 주면 「10개까지 무료」로 읽혀 오해를 부른다.
   */
  const isContractCount = quotaValue === 0 && quotaPerUnit === 1;
  const quotaUnit = String(
    Form.useWatch(['quotas', field.name, 'quotaUnit'], form) ?? DEFAULT_BYTE_UNIT,
  );

  const metric = catalog.metricOf(quotaKey);
  const isBytes = metric?.unit === 'BYTES';
  const shownUnit = isBytes ? quotaUnit : '개';

  /*
   * 같은 지표를 두 줄 넣으면 뒤엣것이 앞엣것을 덮어쓴다
   * (plan_quota 의 기본키가 plan_id + quota_key 다). 고르는 자리에서 막는다.
   */
  const chosen: string[] = (form.getFieldValue('quotas') ?? [])
    .map((q: { quotaKey?: string } | undefined, i: number) =>
      i === field.name ? null : q?.quotaKey,
    )
    .filter(Boolean);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: QUOTA_GRID, gap: 8, alignItems: 'start' }}>
        <Form.Item
          name={[field.name, 'quotaKey']}
          rules={[{ required: true, message: '항목 선택' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            placeholder="한도 항목"
            optionFilterProp="label"
            options={options.map((o) => ({ ...o, disabled: chosen.includes(o.value) }))}
          />
        </Form.Item>

        <Form.Item
          name={[field.name, 'quotaValue']}
          rules={[{ required: true, message: '한도 입력' }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="기본 제공" />
        </Form.Item>

        {/*
          수량당 추가.

          여기를 비우면 예전처럼 **계약 수량과 무관한 고정 한도**가 된다.
          그러면 3인 기업과 100인 기업이 같은 용량을 받는다. 사람 수에 따라
          늘어나야 하는 것(저장 용량 등)은 이 칸에 적는다.
        */}
        <Form.Item name={[field.name, 'quotaPerUnit']} style={{ marginBottom: 0 }}>
          <InputNumber min={0} style={{ width: '100%' }} placeholder="수량당" />
        </Form.Item>

        {isBytes ? (
          <Form.Item name={[field.name, 'quotaUnit']} style={{ marginBottom: 0 }}>
            <Select options={BYTE_UNITS.map((u) => ({ value: u.value, label: u.value }))} />
          </Form.Item>
        ) : (
          <div style={{ lineHeight: '32px', color: '#8c8c8c', fontSize: 13 }}>개</div>
        )}

        <Form.Item
          name={[field.name, 'overageAllowed']}
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Checkbox>허용</Checkbox>
        </Form.Item>

        {overageAllowed ? (
          <Form.Item
            name={[field.name, 'overagePrice']}
            rules={[{ required: true, message: '초과 단가를 입력하세요' }]}
            style={{ marginBottom: 0 }}
          >
            {/*
              원 단위 정수만 받는다.

              청구서 금액에 소수가 붙으면 합계와 부가세가 1원씩 어긋난다.
              더 잘게 매기고 싶으면 금액을 쪼개지 말고 **단위를 키운다** —
              GB 당 0.5원은 TB 당 500원으로 적는다.
            */}
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '100%' }}
              addonAfter={`원 / ${shownUnit}`}
              placeholder="초과 단가"
            />
          </Form.Item>
        ) : (
          <div style={{ lineHeight: '32px', color: '#bfbfbf', fontSize: 12 }}>
            한도에서 막습니다
          </div>
        )}

        <Tooltip title="이 한도 삭제">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            aria-label="한도 삭제"
          />
        </Tooltip>
      </div>

      {/*
        넣은 값이 실제로 얼마가 되는지. 자릿수를 틀리면 여기서 보인다.

        ⚠ 「무료」라고 쓰지 않는다.
        예전에는 「10개까지 무료」라고 적었는데, 두 가지가 한꺼번에 잘못 읽혔다.
          · 10 은 **미리보기용 예시 수량**일 뿐인데 정해진 숫자처럼 보였다
          · 포함량은 요금에 들어 있는 몫이지 공짜가 아니다
        그래서 「사용자 10명이면」처럼 예시임을 밝히고 「포함」이라고 쓴다.
      */}
      {quotaKey && limitAtTen > 0 && (
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4, paddingLeft: 2 }}>
          {isContractCount ? (
            /*
             * 기본 0 + 수량당 1 은 「한도 = 계약 수량」이라는 뜻이다.
             * 이 경우만큼은 예시 숫자를 보여 주면 오해를 부른다 — 계약한 만큼
             * 쓸 수 있다는 말이 전부다.
             */
            <div>
              <b style={{ color: '#595959' }}>계약한 {perUnitLabel} 수만큼</b> 쓸 수 있습니다
              {overageAllowed && overagePrice > 0
                ? ` · 계약보다 많이 쓰면 1${shownUnit} 당 ${won(overagePrice)}원`
                : ' · 계약 수를 넘기면 막힙니다'}
            </div>
          ) : (
            <>
              {/* 계산식을 먼저. 「기본 10 + 사용자당 2」가 몇이 되는지 */}
              {quotaPerUnit > 0 && (
                <div>
                  기본 {won(quotaValue)}
                  {shownUnit} + {perUnitLabel}당 {won(quotaPerUnit)}
                  {shownUnit} →{' '}
                  <b style={{ color: '#595959' }}>
                    {perUnitLabel} 10{perUnitLabel === '사용자' ? '명' : '개'}이면{' '}
                    {won(limitAtTen)}
                    {shownUnit}
                  </b>{' '}
                  (예시)
                </div>
              )}
              {overageAllowed && overagePrice > 0 ? (
                <>
                  {quotaPerUnit > 0 ? '이 포함량' : `${won(quotaValue)}${shownUnit}`} 까지
                  요금에 포함 · 넘기면 1{shownUnit} 당 {won(overagePrice)}원 →{' '}
                  <b style={{ color: '#595959' }}>
                    {won(limitAtTen + 2)}
                    {shownUnit} 쓰면 {won(overagePrice * 2)}원 추가 청구
                  </b>
                  {metric?.aggregation === 'SUM' ? ' (한 달 합계 기준)' : ' (그 시점의 값 기준)'}
                  {/* 올림 규칙을 적어 둔다. 1.2GB 초과를 2GB 로 치는 것을 모르면 청구서가 틀려 보인다. */}
                  {` · 시작된 1${shownUnit} 은 올려서 셉니다`}
                </>
              ) : (
                <>
                  {quotaPerUnit > 0 ? '이 포함량' : `${won(quotaValue)}${shownUnit}`} 까지만 쓸 수
                  있습니다. 넘기면 <b style={{ color: '#595959' }}>막힙니다</b> — 추가 청구는
                  없습니다
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
